import {
  Checkbox,
  Css,
  IconButton,
  LoadingSkeleton,
  MultiSelectField,
  Palette,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { PotentialOperation2 } from "src/generated/graphqlTypes";
import { PublishDraftScheduleStep } from "src/routes/projects/dynamic-schedules/draft-mode/PublishDraftScheduleStepper";
import {
  TradeConfirmPublishStep_PlanTaskFragment,
  TradeConfirmPublishStep_TradePartnerContactFragment,
  TradeConfirmPublishStep_TradePartnerFragment,
  useTradeConfirmPublishStepQuery,
} from "src/routes/projects/dynamic-schedules/draft-mode/TradeConfirmPublishStep.generated";
import { ProjectParams } from "src/routes/routesDef";
import { TradePartnerContactModal } from "src/routes/trade-partners/TradePartnerContactModal";
import { queryResult } from "src/utils";
import { formatMonthDay } from "src/utils/dates";
import { mapToDraftPlanTaskInput, useDraftScheduleStore } from "./scheduleDraftStore";

type TradeConfirmPublishStepProps = {
  updateStepStatus: (currentStepCompleted: boolean, currentStepValue: PublishDraftScheduleStep) => void;
};

export function TradeConfirmPublishStep({ updateStepStatus }: TradeConfirmPublishStepProps) {
  const { projectId } = useParams<ProjectParams>();

  const draftTaskChanges = useDraftScheduleStore((state) => state.draftTaskChanges);
  const userAddedScheduleFlags = useDraftScheduleStore((state) => state.userAddedScheduleFlags);
  const skipStaleTaskAutoPin = useDraftScheduleStore((state) => state.skipStaleTaskAutoPin);
  const currentDate = useDraftScheduleStore((state) => state.currentDate);

  const query = useTradeConfirmPublishStepQuery({
    variables: {
      projectId,
      draftScheduleInput: {
        draftTaskChanges: mapToDraftPlanTaskInput(draftTaskChanges, userAddedScheduleFlags),
        scheduleParentId: projectId,
        skipStaleTaskAutoPin,
        currentDate,
      },
    },
  });

  return queryResult(query, {
    data: ({ draftPlanSchedule }) => {
      return (
        <TradeConfirmPublishStepView
          tradeConfirmPlanTasks={draftPlanSchedule.tradeConfirmPlanTasks}
          canSendTradePartnerAvailabilityRequestEmail={
            draftPlanSchedule.planSchedule.canSendTradePartnerAvailabilityRequestEmail
          }
          updateStepStatus={updateStepStatus}
        />
      );
    },
    loading: () => (
      <div css={Css.m8.$}>
        <LoadingSkeleton columns={3} rows={10} size="lg" />
      </div>
    ),
  });
}

type TradeConfirmPublishStepViewProps = TradeConfirmPublishStepProps & {
  tradeConfirmPlanTasks: TradeConfirmPublishStep_PlanTaskFragment[];
  canSendTradePartnerAvailabilityRequestEmail: PotentialOperation2;
};

function TradeConfirmPublishStepView({
  tradeConfirmPlanTasks,
  canSendTradePartnerAvailabilityRequestEmail,
  updateStepStatus,
}: TradeConfirmPublishStepViewProps) {
  const setTparAvailabilityRequestInput = useDraftScheduleStore((state) => state.setTparAvailabilityRequestInput);

  // local state to handle selecting/unselecting trade partner contacts to send emails to
  const [selectedTaskContacts, setSelectedTaskContacts] = useState<Record<string, string[]>>({});
  const name = useDraftScheduleStore((state) => state.scheduleParentName);

  // Create a map of trade partners with two lists (tasks and trade partners) to easily pluck out data
  const groupedTasks = useMemo(() => {
    // first group by the trade partner id
    const groupedById = tradeConfirmPlanTasks.groupBy((task) => task.tradePartner?.id ?? "");
    // then create two children arrays, one for the trade partner and one for the tasks
    const groupedTasks = Object.entries(groupedById).map(([tradePartnerId, tasks]) => ({
      tradePartner: tasks.find((task) => task.tradePartner?.id === tradePartnerId)?.tradePartner,
      tasks: tasks.map((task) => ({
        id: task.id,
        name: task.name,
        startDate: task.startDate,
        knownSchedulingContacts: task.knownSchedulingContacts ?? [],
      })),
    }));
    return groupedTasks;
  }, [tradeConfirmPlanTasks]);

  // Handler function to update trade partner contacts on select
  const handleSelectedTask = useCallback(
    (
      selected: boolean,
      suggestedContacts: TradeConfirmPublishStep_TradePartnerContactFragment[],
      otherContacts: TradeConfirmPublishStep_TradePartnerContactFragment[],
      taskId: string,
    ) => {
      setSelectedTaskContacts((prevState) => {
        const newState = { ...prevState };
        if (selected) {
          // pre-select the known scheduling contacts if they exist, otherwise use the other contacts
          newState[taskId] = suggestedContacts.nonEmpty
            ? suggestedContacts.map((c) => c.id)
            : otherContacts.map((c) => c?.id ?? "");
        } else {
          // Remove the task key/value from local state
          delete newState[taskId];
        }
        return newState;
      });
    },
    [setSelectedTaskContacts],
  );

  useEffect(() => {
    const formattedTparInput = groupedTasks.flatMap(({ tasks, tradePartner }) => {
      // Map tasks to create trade partner availability request input shape
      return tasks.map((task) => ({
        taskId: task.id,
        tradePartnerId: tradePartner?.id ?? "",
        tradePartnerContactIds: selectedTaskContacts[task.id],
      }));
    });
    setTparAvailabilityRequestInput(formattedTparInput);
  }, [groupedTasks, selectedTaskContacts, setTparAvailabilityRequestInput]);

  useEffect(() => {
    const hasValidSelections =
      Object.keys(selectedTaskContacts).nonEmpty &&
      Object.values(selectedTaskContacts).every((contactIds) => contactIds.nonEmpty);

    // Only mark step complete if sending is allowed and the user has valid contact selections
    updateStepStatus(
      hasValidSelections && canSendTradePartnerAvailabilityRequestEmail.allowed,
      PublishDraftScheduleStep.TradeConfirm,
    );
  }, [selectedTaskContacts, updateStepStatus, canSendTradePartnerAvailabilityRequestEmail.allowed]);

  // Prepopulate known scheduling contacts in the dropdown (so users don't have to manually select).
  // When sending is not allowed, skip pre-population so the step stays "incomplete" and the
  // Publish button remains disabled — the user can still "Skip and Publish" to save without notifications.
  useEffect(() => {
    if (groupedTasks.nonEmpty && canSendTradePartnerAvailabilityRequestEmail.allowed) {
      const tasks = groupedTasks.flatMap(({ tasks }) => tasks);
      tasks.forEach((task) => {
        if (task.knownSchedulingContacts.nonEmpty) {
          // Pre-select known scheduling contacts
          setSelectedTaskContacts((prevState) => ({
            ...prevState,
            [task.id]: task.knownSchedulingContacts.map((contact) => contact.id),
          }));
        }
      });
    }
  }, [groupedTasks, setSelectedTaskContacts, canSendTradePartnerAvailabilityRequestEmail.allowed]);

  return (
    <div css={Css.maxwPx(1200).ma.tac.mt6.$} data-testid="tradeConfirmPublishStep">
      <h6 css={Css.md.gray700.mb1.$}>{name}</h6>
      <h1 css={Css.xl2.$}>Select Trades to Notify</h1>
      <p css={Css.md.my2.mb5.$}>
        The following Trade Partners are associated to the tasks that have been updated. Select the ones that you want
        to notify. The notifications will inform trades of the date that they should be available for their assigned
        task(s).
      </p>
      {groupedTasks.isEmpty ? (
        <TradePartnerAvailabilityRequestEmptyState />
      ) : (
        <div css={Css.dg.gtc("1fr 100px 120px 300px").rg1.cg3.bgWhite.p4.maxwPx(1000).br8.ma.$}>
          <span css={Css.smSb.tal.$}>Trade Partner</span>
          <span css={Css.smSb.tal.$}>Original Date</span>
          <span css={Css.smSb.tal.$}>Updated Date</span>
          <span css={Css.smSb.tal.$}>Contact</span>
          {groupedTasks.map(({ tasks, tradePartner }) => {
            const otherTradePartnerContacts = tradePartner?.contactsForMarket ?? [];
            return (
              <TradePartnerColumnData
                key={tradePartner?.id}
                tasks={tasks}
                tradePartner={tradePartner}
                handleSelectedTask={handleSelectedTask}
                selectedTaskContacts={selectedTaskContacts}
                setSelectedTaskContacts={setSelectedTaskContacts}
                otherTradePartnerContacts={otherTradePartnerContacts.compact()}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function TradePartnerAvailabilityRequestEmptyState() {
  return (
    <div
      css={
        Css.df.jcc.aic.bgGray200.gray700.p4
          .wPx(731)
          .hPx(515)
          .br8.boxShadow("0px 4px 8px 0px rgba(53, 53, 53, 0.08), 0px 2px 16px 0px rgba(53, 53, 53, 0.03)").ma.gap3.sm.$
      }
      data-testid="tradePartnerAvailabilityRequestEmptyState"
    >
      The changes made do not require any Trade <br /> Partner availability updates.
    </div>
  );
}

type TradePartnerColumnDataProps = {
  tasks: TradeConfirmPublishStep_PlanTaskFragment[];
  tradePartner: TradeConfirmPublishStep_TradePartnerFragment | null | undefined;
  handleSelectedTask: (
    selected: boolean,
    suggestedContacts: TradeConfirmPublishStep_TradePartnerContactFragment[],
    otherContacts: TradeConfirmPublishStep_TradePartnerContactFragment[],
    taskId: string,
  ) => void;
  selectedTaskContacts: Record<string, string[]>;
  setSelectedTaskContacts: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  otherTradePartnerContacts: TradeConfirmPublishStep_TradePartnerContactFragment[];
};

function TradePartnerColumnData({
  tasks,
  tradePartner,
  handleSelectedTask,
  selectedTaskContacts,
  setSelectedTaskContacts,
  otherTradePartnerContacts,
}: TradePartnerColumnDataProps) {
  const tids = useTestIds({}, "tradeConfirmStep");
  const getInitialTaskValue = useDraftScheduleStore((state) => state.getInitialTaskValue);

  return (
    <Fragment key={tradePartner?.id}>
      <div css={Css.df.fdc.gap2.tal.gc(1).mt1.$}>
        <label css={Css.df.aic.gap2.sm.gray900.$}>
          {tradePartner ? (
            <span css={Css.sm.gray900.$} {...tids.tradeName}>
              {tradePartner.name}
            </span>
          ) : (
            "No Trade Partner"
          )}
        </label>
      </div>

      {tasks.map((task) => {
        // Combine known scheduling contacts with other trade partner market contacts
        const tradePartnerContacts = formatTradePartnerContacts(
          task.knownSchedulingContacts,
          otherTradePartnerContacts,
        );

        const initialTaskValue = getInitialTaskValue(task.id);

        return (
          <Fragment key={task.id}>
            <div css={Css.sm.gray700.mb1.tal.truncate.gc(1).df.aic.gap2.$} {...tids.taskName}>
              <Checkbox
                label=""
                checkboxOnly
                selected={!!(tradePartner && selectedTaskContacts[task.id])}
                onChange={(selected) =>
                  handleSelectedTask(selected, task.knownSchedulingContacts, otherTradePartnerContacts, task.id)
                }
                disabled={otherTradePartnerContacts?.isEmpty}
              />
              {task.name}
            </div>
            <div css={Css.gc(2).mb1.tal.$} {...tids.taskInitialDate}>
              {initialTaskValue ? formatMonthDay(initialTaskValue.startDate) : "--"}
            </div>
            <div css={Css.gc(3).mb1.tal.$} {...tids.taskNewDate}>
              {formatMonthDay(task.startDate)}
            </div>
            <TradePartnerContactColumn
              tradePartner={tradePartner}
              selectedTaskContacts={selectedTaskContacts}
              setSelectedTaskContacts={setSelectedTaskContacts}
              tradePartnerContacts={tradePartnerContacts ?? []}
              taskId={task.id}
            />
          </Fragment>
        );
      })}
    </Fragment>
  );
}

type TradePartnerContactColumnProps = {
  tradePartner: TradeConfirmPublishStep_TradePartnerFragment | null | undefined;
  selectedTaskContacts: Record<string, string[]>;
  setSelectedTaskContacts: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  tradePartnerContacts: TradeConfirmPublishStep_TradePartnerContactFragment[];
  taskId: string;
};

function TradePartnerContactColumn({
  tradePartner,
  setSelectedTaskContacts,
  selectedTaskContacts,
  tradePartnerContacts,
  taskId,
}: TradePartnerContactColumnProps) {
  const { openModal } = useModal();

  return (
    <div css={Css.df.gap2.aic.$}>
      {tradePartnerContacts?.nonEmpty ? (
        <div css={Css.tal.df.aic.gap2.$}>
          <MultiSelectField
            label="tradePartnerContactIds"
            labelStyle="hidden"
            getOptionLabel={({ name, email }) => (email ? `${name}, <${email}>` : name)}
            getOptionValue={({ id }) => id}
            // Disable the "suggested + other" fake rows so users cannot select them
            disabledOptions={tradePartnerContacts.filter((contact) => !contact.email).map((contact) => contact.id)}
            onSelect={(selectedContacts) => {
              setSelectedTaskContacts((prevState) => ({ ...prevState, [taskId]: selectedContacts }));
            }}
            options={tradePartnerContacts}
            values={selectedTaskContacts[taskId] ?? []}
          />
          <IconButton
            icon="plus"
            onClick={() =>
              openModal({
                size: "lg",
                content: (
                  <TradePartnerContactModal
                    tradePartnerId={tradePartner?.id!}
                    markets={tradePartner?.markets ?? []}
                    contact={undefined}
                    onTradeConfirmPublishStep
                  />
                ),
              })
            }
            color={Palette.Blue600}
          />
        </div>
      ) : (
        <span>No contact information available for this trade partner</span>
      )}
    </div>
  );
}

export function formatTradePartnerContacts(
  suggestedContacts: TradeConfirmPublishStep_TradePartnerContactFragment[],
  otherContacts: TradeConfirmPublishStep_TradePartnerContactFragment[],
): TradeConfirmPublishStep_TradePartnerContactFragment[] {
  // Dedupe the other contacts by filtering out those that are already in the "suggested" list
  const dedupedOtherContacts = otherContacts?.filter(
    (otherContact) => !suggestedContacts.some((suggestedContact) => suggestedContact.id === otherContact?.id),
  );

  // Combine "Suggested" and deduped "Other" contacts
  return [
    ...(suggestedContacts.nonEmpty
      ? [{ id: "suggested-placeholder", name: "Suggested", email: null }, ...suggestedContacts]
      : []),
    ...(dedupedOtherContacts?.nonEmpty
      ? [{ id: "other-placeholder", name: "Other", email: null }, ...dedupedOtherContacts]
      : []),
  ];
}
